import { resolveLookupKeys, resolveAnswer } from './components/base.jsx';
import Resolver from './component_resolver.jsx';
import * as FormComponent from './form_components.jsx';
import Helpers from './components/helpers.jsx';
import { CONTEXT_OPTIONS } from './common.jsx';

function isRepeatableRegionType(compType) {
  const cls = Resolver.componentClass(compType);
  if (!cls) return false;
  return cls === FormComponent.RepeatableRegion || cls.prototype instanceof FormComponent.RepeatableRegion;
}

// Port of base.jsx:857-874 as a pure function
function loadAnswerFromDatagrid(lookupBy, dependencyMapping, rows, columnAccessor) {
  if (Object.keys(lookupBy).length !== dependencyMapping.length) return undefined;

  const loadFrom = rows.find(row =>
    Object.keys(lookupBy).every(colId => row[colId] == lookupBy[colId])
  );

  if (!loadFrom) return '#NOVALUE';
  if (loadFrom[columnAccessor]) return loadFrom[columnAccessor];
  return undefined;
}

function buildPath(fieldScope, repeatedFieldScope, fieldName) {
  return Helpers.fieldNamePath(
    Helpers.fullFieldName(fieldScope, repeatedFieldScope, fieldName, false, null),
    fieldScope
  );
}

function resolveDatagridRow(comp, answers, fieldScope, repeatedFieldScope, idToName) {
  if (!comp.datagridRowLookupDependency) return;

  const dependencyName = idToName[comp.datagridRowLookupDependency];
  if (!dependencyName) return;

  const { linkedAnswerWithinRegion, linkedAnswerOutside } = resolveAnswer(
    fieldScope, repeatedFieldScope, dependencyName, answers
  );
  let rowLookupAnswer = linkedAnswerWithinRegion || linkedAnswerOutside;
  if (!rowLookupAnswer) return;

  // DatagridRowLookup stores the row object; may be serialized as JSON string on reload
  if (typeof rowLookupAnswer === 'string') {
    try { rowLookupAnswer = JSON.parse(rowLookupAnswer); } catch (e) { return; }
  }
  if (typeof rowLookupAnswer !== 'object' || rowLookupAnswer === null) return;

  const depAnswer = rowLookupAnswer[comp.columnAccessor];
  if (depAnswer === undefined || depAnswer === null) return;

  // Text fields get the label when the column value is an object (matches base.jsx:821)
  const newAnswer = depAnswer?.constructor === Object && comp.type === 'text_field'
    ? depAnswer.label : depAnswer;

  const path = buildPath(fieldScope, repeatedFieldScope, comp.name);
  Helpers.setObjectValue(answers, [...path], newAnswer);
}

function resolveDatagridPreset(comp, answers, formContext, fieldScope, repeatedFieldScope) {
  const { datagridId, presetId, columnAccessor, dependencyMapping = [] } = comp;

  const rows = formContext.datagridRows?.[datagridId]?.[presetId]?.rows;
  if (!rows) return;

  const lookupBy = resolveLookupKeys(dependencyMapping, fieldScope, repeatedFieldScope, answers);
  if (lookupBy === undefined) return; // a dependency answer is still missing

  const path = buildPath(fieldScope, repeatedFieldScope, comp.name);
  const currentAnswer = Helpers.getObjectValue(answers, path);

  // Guard: no dependencies and answer already set → preserve user-entered value
  if (dependencyMapping.length === 0 && currentAnswer && currentAnswer !== '#NOVALUE') return;

  const answer = loadAnswerFromDatagrid(lookupBy, dependencyMapping, rows, columnAccessor);
  if (answer === undefined) return;
  if (answer === currentAnswer) return;

  Helpers.setObjectValue(answers, [...path], answer);
}

// Recursively walk the form component tree, collecting datagrid-dependent components
// with their resolved field scope. Handles repeatable regions (row iteration) and
// grid-layout-style containers (GridLayout, OptionalRegion — same answer scope).
function collectComponents(comp, fieldScope, repeatedFieldScope, answers, allFields) {
  const ctx = comp.useContextual;

  if (ctx === CONTEXT_OPTIONS.DATAGRID_ROW || ctx === CONTEXT_OPTIONS.DATAGRID_PRESET) {
    allFields.push({ comp, repeatedFieldScope });
  }

  if (isRepeatableRegionType(comp.type)) {
    // DatagridPrefilledRepeatableRegion (identified by datagridId + presetId on the region
    // itself): its buildPrefillRows handles all child resolution using row-indexed datagrid
    // access. The resolver has no way to know which datagrid row maps to which repeatable
    // row, so touching these children would write wrong values and cause loops with
    // resetIfMissingFromOptions.
    if (comp.datagridId != null && comp.presetId != null) return;

    // Regular RepeatableRegion: children answers are namespaced under
    // answers[regionName][rowId][childName]; discover rows from existing answers.
    const regionPath = buildPath(fieldScope, repeatedFieldScope, comp.name);
    const regionAnswers = Helpers.getObjectValue(answers, regionPath) || {};
    const rowIds = Object.keys(regionAnswers).filter(k => /^row-\d+$/.test(k));
    const templateComp = comp.component;

    if (templateComp && Resolver.componentClass(templateComp.type)) {
      rowIds.forEach(rowId => {
        const childScope = repeatedFieldScope
          ? `${repeatedFieldScope}[${comp.name}[${rowId}]]`
          : `${comp.name}[${rowId}]`;
        collectComponents(templateComp, fieldScope, childScope, answers, allFields);
      });
    }
  } else if (comp.rows && Array.isArray(comp.rows)) {
    // GridLayout / OptionalRegion — children share the same answer scope as the container
    comp.rows.forEach(row => {
      (row || []).forEach(col => {
        if (col.component && Resolver.componentClass(col.component.type)) {
          collectComponents(col.component, fieldScope, repeatedFieldScope, answers, allFields);
        }
      });
    });
  }
  // Leaf components: no further recursion needed
}

// Topological sort of collected fields so each field is resolved after all fields it
// depends on. DATAGRID_ROW fields have no resolver-level dependencies and sort first.
// DATAGRID_PRESET fields depend on other fields named in their dependencyMapping.
// Fields are grouped by component name: if B depends on A, all instances of A (across
// all row scopes) are processed before all instances of B.
// Falls back to original order on cycles (should not occur in valid form configs).
function topoSortFields(fields) {
  // Map comp name → indices in fields array
  const nameToIndices = new Map();
  fields.forEach(({ comp }, i) => {
    if (!nameToIndices.has(comp.name)) nameToIndices.set(comp.name, []);
    nameToIndices.get(comp.name).push(i);
  });

  const uniqueNames = [...nameToIndices.keys()];

  // Build dep graph: name → names it depends on (only those present in allFields)
  const inDegree = new Map(uniqueNames.map(n => [n, 0]));
  const dependents = new Map(uniqueNames.map(n => [n, []]));

  uniqueNames.forEach(name => {
    // Find one representative comp for this name to read its dependencyMapping
    const idx = nameToIndices.get(name)[0];
    const { comp } = fields[idx];
    (comp.dependencyMapping || []).forEach(({ componentId: depName }) => {
      if (nameToIndices.has(depName)) {
        inDegree.set(name, inDegree.get(name) + 1);
        dependents.get(depName).push(name);
      }
    });
  });

  const queue = uniqueNames.filter(n => inDegree.get(n) === 0);
  const sortedNames = [];
  while (queue.length > 0) {
    const name = queue.shift();
    sortedNames.push(name);
    dependents.get(name).forEach(dep => {
      inDegree.set(dep, inDegree.get(dep) - 1);
      if (inDegree.get(dep) === 0) queue.push(dep);
    });
  }

  // Cycle detected — fall back to original order
  if (sortedNames.length < uniqueNames.length) return fields;

  const result = [];
  sortedNames.forEach(name => nameToIndices.get(name).forEach(i => result.push(fields[i])));
  return result;
}

export function resolveDatagridDerivedAnswers(answers, formContext, form, fieldScope, skipPresetUpdate = false) {
  if (!form?.pages?.[0]?.components) return answers;

  const components = form.pages[0].components;
  const newAnswers = { ...answers };

  // Build id→name map for resolving datagridRowLookupDependency IDs
  const idToName = {};
  components.forEach(comp => {
    [comp, ...(Resolver.componentClass(comp.type)?.nestedComponents(comp) ?? [])].forEach(c => {
      if (c.id && c.name) idToName[c.id] = c.name;
    });
  });

  const allFields = [];
  components.forEach(comp => {
    collectComponents(comp, fieldScope, null, newAnswers, allFields);
  });

  topoSortFields(allFields).forEach(({ comp, repeatedFieldScope }) => {
    if (comp.useContextual === CONTEXT_OPTIONS.DATAGRID_ROW) {
      resolveDatagridRow(comp, newAnswers, fieldScope, repeatedFieldScope, idToName);
    } else if (!skipPresetUpdate) {
      resolveDatagridPreset(comp, newAnswers, formContext, fieldScope, repeatedFieldScope);
    }
  });

  return newAnswers;
}
